import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BoltIcon from "@mui/icons-material/Bolt";
import VpnKeyOffIcon from "@mui/icons-material/VpnKeyOff";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import type { FilterPanelState } from "@/types";
import {
  colors,
  accordionStyles,
  accordionSummaryStyles,
  accordionDetailsStyles,
} from "@/theme/eventsTokens.ts";
import { FilterBadge } from "./FilterWidgets.tsx";
import {
  SCENARIOS,
  applyScenario,
  isScenarioActive,
  resetScenario,
  type ScenarioConfig,
} from "./scenarios.ts";

const ICON_MAP: Record<string, React.ReactNode> = {
  VpnKeyOff: <VpnKeyOffIcon sx={{ fontSize: 18 }} />,
  PersonSearch: <PersonSearchIcon sx={{ fontSize: 18 }} />,
};

interface PanelProps {
  readonly expanded: boolean;
  readonly onToggle: (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  readonly filters: FilterPanelState;
  readonly onFiltersChange: (filters: FilterPanelState) => void;
}

const cardBase = {
  width: "100%",
  borderRadius: "8px",
  border: `1px solid ${colors.border}`,
  padding: "10px 12px",
  textAlign: "left" as const,
  transition: "all 0.15s ease",
  cursor: "pointer",
  "&:hover": {
    borderColor: colors.textLight,
    backgroundColor: colors.backgroundSecondary,
  },
} as const;

const cardActive = {
  borderColor: colors.primary,
  backgroundColor: colors.backgroundMuted,
  boxShadow: `inset 3px 0 0 ${colors.primary}`,
  "&:hover": {
    borderColor: colors.primary,
    backgroundColor: colors.backgroundMuted,
  },
} as const;

function ScenarioCard({
  scenario,
  active,
  onClick,
}: {
  readonly scenario: ScenarioConfig;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{ ...cardBase, ...(active ? cardActive : {}) }}
      disableRipple
    >
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ color: active ? colors.primary : colors.textMuted }}>
            {ICON_MAP[scenario.icon] ?? <BoltIcon sx={{ fontSize: 18 }} />}
          </Box>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: active ? colors.primary : colors.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {scenario.label}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            color: colors.textMuted,
            lineHeight: 1.4,
            pl: 3.25,
          }}
        >
          {scenario.description}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

export function ScenariosPanel({ expanded, onToggle, filters, onFiltersChange }: PanelProps) {
  const activeCount = SCENARIOS.filter((s) => isScenarioActive(filters, s)).length;

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-scenarios">
        <FilterBadge
          count={activeCount}
          icon={<BoltIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Scenarios"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <Stack spacing={1}>
          {SCENARIOS.map((scenario) => {
            const active = isScenarioActive(filters, scenario);
            return (
              <ScenarioCard
                key={scenario.label}
                scenario={scenario}
                active={active}
                onClick={() =>
                  onFiltersChange(
                    active
                      ? resetScenario(filters, scenario)
                      : applyScenario(filters, scenario),
                  )
                }
              />
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
